/**
 * What the enroll screens say about the install command, and the requirements
 * that decide whether it will work.
 *
 * A plain module rather than constants inside the wizard step, for the reason
 * `log-source-table-frame` exists: the step is `lazy()`, and the eager skeleton
 * has to render the same chrome — importing from the step would drag its chunk
 * into the eager bundle.
 *
 * The command itself is never built here. The API returns it as one finished
 * line, and the screens show it exactly as it arrives: a line break, a quote or
 * a trimmed space added client-side breaks the token header, and the endpoint
 * answers a mangled token with the same 404 it gives an expired one.
 */
import { formatDate } from '@/shared/datetime/utils/format'

/** What the code block's header chip says, and the shell the command assumes. */
export const INSTALL_COMMAND_SHELL = 'bash'

/**
 * The hint under the name field. It says where the name shows up next, because
 * it is a *label* for a site and not a hostname — an operator who thinks it
 * must match the machine will type something nobody can pick from a list.
 */
export const COLLECTOR_NAME_HINT =
	'Name this site or location. You’ll pick it when adding log sources.'

/**
 * How long a minted enrollment token stays redeemable (the backend's
 * `ENROLLMENT_TOKEN_TTL_HOURS`). Twelve, not twenty-four: copy that promised a
 * day would send someone back tomorrow to a command that no longer works.
 */
export const ENROLLMENT_TOKEN_TTL_HOURS = 12

const EXPIRY_FORMAT = 'MMM d, h:mm a'

/**
 * The line under the install command: how long it works, and until when.
 *
 * States the duration *and* the moment, because "twelve hours" alone does not
 * say whether a command minted this morning still works tonight. Says it is
 * single-use and not shown again, because an engineer who closes the page
 * expecting to copy it later has to know to issue a new one.
 *
 * @param expiresAt ISO 8601, from `enrollment_token_expires_at`.
 */
export function describeTokenValidity(expiresAt: string): string {
	return `Valid for ${ENROLLMENT_TOKEN_TTL_HOURS} hours, until ${formatDate(expiresAt, EXPIRY_FORMAT)}. It works once and is not shown again — if it is lost, issue a new token from the collector’s page.`
}

/** The same line once the token has lapsed. Never re-mints on its own. */
export function describeTokenExpired(expiresAt: string): string {
	return `This command expired at ${formatDate(expiresAt, EXPIRY_FORMAT)} and will no longer install the collector. Issue a new one to continue.`
}

/**
 * What the check-in wait says about the command when it gives up — that it
 * still works. Giving up is the screen's patience running out, not the token
 * expiring, and an engineer who conflates the two re-mints a working command
 * and retires the one already pasted on the host.
 */
export function describeTokenStillValid(expiresAt: string): string {
	return `The install command has not expired — it still works until ${formatDate(expiresAt, EXPIRY_FORMAT)}.`
}

/**
 * What the host must be before the one-liner can succeed.
 *
 * `critical` is the one an operator learns about too late: DHCP does not fail
 * loudly at install time on every distro, and a Collector whose address moves
 * stops receiving syslog with no error anywhere — so it is the one phrase on the
 * line that carries colour, exactly as the Collector detail page does with the
 * same fact.
 */
export const COLLECTOR_HOST_REQUIREMENTS = [
	{ label: 'Linux VM (Debian / Ubuntu)', critical: false },
	{ label: 'Outbound HTTPS (443) only', critical: false },
	{ label: 'Static IP required — DHCP blocks install', critical: true },
] as const

/**
 * What to check when the wait for a first check-in gives up.
 *
 * Ordered by where the failure shows: the installer's own output first,
 * because install errors appear in the host's terminal and never in AIR®, then
 * the two host facts that fail quietly.
 */
export const CHECK_IN_TROUBLESHOOTING = [
	'The install command finished without errors in the host’s terminal.',
	'The host can reach AIR® over outbound HTTPS (port 443).',
	'The host has a static IP — DHCP blocks the install.',
] as const
